import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UploadCloud, CheckCircle2 } from "lucide-react";

export default function PartnerApply() {
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(null); // { email, name, token }
  const [form, setForm] = useState({ full_name: "", phone: "", profession: "", address: "" });
  const [panFile, setPanFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  const handleGoogleCredential = async (credential) => {
    setErr("");
    setVerifying(true);
    try {
      const { data } = await api.post("/partner/verify-email", { credential });
      setVerified({ email: data.email, name: data.name, token: data.email_verification_token });
      setForm((f) => ({ ...f, full_name: f.full_name || data.name || "" }));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setVerifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.full_name.trim() || !form.phone.trim() || !form.profession.trim() || !form.address.trim()) {
      setErr("All fields are required.");
      return;
    }
    if (!panFile) {
      setErr("Please attach your PAN card.");
      return;
    }
    setSubmitting(true);
    try {
      const body = new FormData();
      body.append("full_name", form.full_name.trim());
      body.append("phone", form.phone.trim());
      body.append("profession", form.profession.trim());
      body.append("address", form.address.trim());
      body.append("email_verification_token", verified.token);
      body.append("pan_card", panFile);
      await api.post("/partner/apply", body);
      setSubmitted(true);
      toast.success("Application submitted!");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="card-brutal p-8">
          <span className="label-mono">Partner Program</span>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter mt-2">Become a Partner</h1>
          <p className="text-sm text-[#52525B] mt-2">
            Get your own referral link — anyone you refer gets ₹100 off the assessment, and you can track
            your signups and earnings once approved.
          </p>

          {submitted ? (
            <div data-testid="partner-apply-success" className="mt-8 card-brutal p-6 bg-[#A7F3D0] flex items-start gap-3">
              <CheckCircle2 size={22} strokeWidth={2.5} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-xl font-extrabold tracking-tight">Application received</div>
                <div className="text-sm mt-1">Our team will review your details and email you once approved.</div>
              </div>
            </div>
          ) : !verified ? (
            <div className="mt-8">
              <p className="label-mono mb-3">Step 1 · Verify your email with Google</p>
              <GoogleSignInButton onCredential={handleGoogleCredential} />
              {verifying && (
                <div className="mt-3 text-sm flex items-center gap-2">
                  <Loader2 className="animate-spin" size={16} /> Verifying…
                </div>
              )}
              {err && <div data-testid="partner-apply-error" className="mt-4 text-sm text-red-700 bg-red-100 border-2 border-red-700 rounded-lg px-3 py-2">{err}</div>}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <p className="label-mono">Step 2 · Your details</p>
              <div>
                <label className="label-mono block mb-2">Email (verified)</label>
                <input disabled value={verified.email} className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-[#F4F4F5] text-[#52525B]" />
              </div>
              <div>
                <label className="label-mono block mb-2">Full name</label>
                <input
                  data-testid="partner-apply-name"
                  required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-mono block mb-2">Phone number</label>
                  <input
                    data-testid="partner-apply-phone"
                    required
                    inputMode="numeric"
                    placeholder="10-digit mobile number"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                    className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="label-mono block mb-2">Profession</label>
                  <input
                    data-testid="partner-apply-profession"
                    required
                    placeholder="e.g. Career Counselor"
                    value={form.profession}
                    onChange={(e) => setForm({ ...form, profession: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="label-mono block mb-2">Address</label>
                <textarea
                  data-testid="partner-apply-address"
                  required
                  rows={2}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="label-mono block mb-2">PAN card</label>
                <label
                  data-testid="partner-apply-pan-dropzone"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#0A0A0A] rounded-xl py-8 cursor-pointer hover:bg-[#FAFAF9]"
                >
                  <UploadCloud size={22} strokeWidth={2.5} />
                  <div className="font-semibold text-sm">{panFile ? panFile.name : "Tap to attach PAN card"}</div>
                  <div className="text-xs text-[#52525B]">PDF, JPG or PNG · under 5MB</div>
                  <input
                    data-testid="partner-apply-pan-input"
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    onChange={(e) => setPanFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
              </div>

              {err && <div data-testid="partner-apply-error" className="text-sm text-red-700 bg-red-100 border-2 border-red-700 rounded-lg px-3 py-2">{err}</div>}

              <button type="submit" data-testid="partner-apply-submit" disabled={submitting} className="btn-brutal bg-blue-600 text-white w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
                {submitting && <Loader2 className="animate-spin" size={16} />} Submit application
              </button>
            </form>
          )}

          <p className="mt-6 text-sm text-center">
            Already a partner? <Link to="/login" className="font-semibold underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
